import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_IMG = "/images/hero-lakeside-retreat.webp";

const MARQUEE_ITEMS = [
  "Property Acquisition",
  "Land Research",
  "Property Assemblages",
  "Zoning & Title Review",
  "Redevelopment Potential",
  "Owner Conversations",
];

const HEADLINE_LINES = ["Real estate opportunity", "is rarely just about the", "property you see today."];

export const Hero = () => {
  return (
    <>
      <section id="top" data-testid="hero-section" className="relative min-h-[92vh] flex flex-col bg-[#111513]">
        <div className="absolute inset-0 overflow-hidden">
          <motion.img
            src={HERO_IMG}
            alt="Modern lakeside retreat in misty Pacific Northwest mountains"
            className="w-full h-full object-cover"
            fetchPriority="high"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(17,21,19,0.78) 0%, rgba(17,21,19,0.42) 45%, rgba(17,21,19,0.85) 100%)",
            }}
            aria-hidden="true"
          />
        </div>
        <div className="relative flex-1 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-40 pb-28 sm:pb-32">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-[11px] sm:text-xs font-semibold tracking-[0.3em] uppercase text-[#A77A50]"
            >
              Choose Me Properties — Washington
            </motion.p>
            <h1 className="mt-6 font-display text-[42px] sm:text-6xl lg:text-[72px] text-[#F4F1EA] leading-[1.08] max-w-4xl">
              {HEADLINE_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {i === 2 ? (
                      <>
                        property you <span className="italic text-[#A77A50]">see today.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
            >
              <p className="mt-7 text-base sm:text-lg text-[#D9D6CE] leading-relaxed max-w-xl">
                We research land, ownership, development potential and changing neighborhoods to uncover what a
                property could become.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact"
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center justify-center gap-2 bg-[#F4F1EA] text-[#111513] text-[13px] font-semibold tracking-[0.1em] uppercase px-8 py-4 hover:bg-white transition-colors"
                >
                  Explore Your Property
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                </a>
                <a
                  href="#what-we-do"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 border border-[#F4F1EA]/35 text-[#F4F1EA] text-[13px] font-semibold tracking-[0.1em] uppercase px-8 py-4 hover:border-[#A77A50] hover:text-[#A77A50] transition-colors"
                >
                  What We Do
                  <ArrowDown className="w-4 h-4" aria-hidden="true" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          aria-hidden="true"
        >
          <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-[#D9D6CE]/70">Scroll</span>
          <motion.span
            className="block w-px h-10 bg-[#D9D6CE]/50 origin-top"
            animate={{ scaleY: [1, 0.4, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </section>
      <div className="bg-[#F4F1EA] border-b border-[#D9D6CE] py-4 overflow-hidden" aria-hidden="true">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {MARQUEE_ITEMS.map((item) => (
                <span key={`${copy}-${item}`} className="flex items-center">
                  <span className="text-[11px] sm:text-xs font-semibold tracking-[0.22em] uppercase text-[#718477] px-7">
                    {item}
                  </span>
                  <span className="w-1.5 h-1.5 rotate-45 bg-[#A77A50]/70 inline-block" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
